// frontend-react/src/pages/CompanyManagementPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Tag,
  Switch,
  Radio,
  Select,
  Space,
  Popconfirm,
  message,
  Row,
  Col,
} from 'antd';
import {
  DollarOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
  DiffOutlined, AuditOutlined, UploadOutlined, BulbOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { getApiBaseUrl } from '../config/api';
import PageLayout from '../components/PageLayout';
import ModelPricingModal from '../components/ModelPricingModal';

interface CompanyItem {
  id: number;
  name: string;
  code: string;
  monthly_cost_usd?: number;
  is_active: boolean;
  max_users: number | null;
  used_users: number;
  admin_email?: string;
  admin_name?: string;
  features?: {
    ad_review_enabled: boolean;
    ai_rules_enabled: boolean;
    ai_ruleset_upload_enabled: boolean;
    legaltech_library_enabled: boolean;
    ai_rule_optimization_enabled: boolean;
    contract_comparison_enabled: boolean;
  };
}

interface MemberItem {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
}

type AdminMode = 'edit' | 'assign' | 'create';

const CompanyManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyItem | null>(null);
  const [adminMode, setAdminMode] = useState<AdminMode>('edit');
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const editAdReviewEnabled = Form.useWatch('ad_review_enabled', editForm);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);

  const apiBase = getApiBaseUrl();

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const [companiesRes, costsRes] = await Promise.all([
        apiClient.get(`${apiBase}/api/v1/platform/companies`),
        apiClient
          .get(`${apiBase}/api/v1/platform/billing/all-companies`)
          .catch(() => ({ data: { companies: [] } })),
      ]);

      const costMap: Record<number, number> = {};
      (costsRes.data.companies || []).forEach((c: { company_id: number; monthly_cost_usd: number }) => {
        costMap[c.company_id] = c.monthly_cost_usd;
      });

      const companies = (companiesRes.data.companies || []).map((c: CompanyItem) => ({
        ...c,
        monthly_cost_usd: costMap[c.id] ?? 0,
      }));

      setCompanies(companies);
    } catch {
      message.error('載入公司列表失敗');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const fetchMembers = useCallback(
    async (companyId: number) => {
      setMembersLoading(true);
      try {
        const res = await apiClient.get(
          `${apiBase}/api/v1/platform/companies/${companyId}/members`
        );
        setMembers(res.data.members);
      } catch {
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    },
    [apiBase]
  );

  useEffect(() => {
    if (user?.is_superadmin) fetchCompanies();
  }, [user, fetchCompanies]);

  if (!user?.is_superadmin) {
    return <Navigate to='/' replace />;
  }

  const handleCreateCompany = async (values: {
    name: string;
    code: string;
    max_users?: number;
    admin_email?: string;
    admin_name?: string;
  }) => {
    try {
      await apiClient.post(`${apiBase}/api/v1/platform/companies`, values);
      message.success('公司已建立');
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchCompanies();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '建立公司失敗');
    }
  };

  const handleToggleActive = async (record: CompanyItem) => {
    try {
      await apiClient.put(`${apiBase}/api/v1/platform/companies/${record.id}`, {
        name: record.name,
        max_users: record.max_users ?? null,
        is_active: !record.is_active,
      });
      message.success(`公司已${!record.is_active ? '啟用' : '停用'}`);
      fetchCompanies();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '狀態更新失敗');
    }
  };

  const handleEditCompany = async (values: any) => {
    if (!editingCompany) return;
    try {
      // 1. 更新公司基本資訊
      await apiClient.put(`${apiBase}/api/v1/platform/companies/${editingCompany.id}`, {
        name: values.name,
        max_users: values.max_users ?? null,
        is_active: editingCompany.is_active,
      });

      // 2. 更新功能開關
      await apiClient.put(`${apiBase}/api/v1/platform/companies/${editingCompany.id}/features`, {
        ad_review_enabled: values.ad_review_enabled ?? true,
        ai_rules_enabled: values.ai_rules_enabled ?? false,
        ai_ruleset_upload_enabled: values.ai_ruleset_upload_enabled ?? false,
        legaltech_library_enabled: values.legaltech_library_enabled ?? false,
        ai_rule_optimization_enabled: values.ai_rule_optimization_enabled ?? false,
        contract_comparison_enabled: values.contract_comparison_enabled ?? false,
      });

      // 3. 處理 Admin 變更
      if (adminMode === 'edit' && (values.admin_email || values.admin_name)) {
        await apiClient.post(
          `${apiBase}/api/v1/platform/companies/${editingCompany.id}/change-admin`,
          {
            mode: 'edit',
            admin_email: values.admin_email || undefined,
            admin_name: values.admin_name || undefined,
          }
        );
      } else if (adminMode === 'assign' && values.assign_user_id) {
        await apiClient.post(
          `${apiBase}/api/v1/platform/companies/${editingCompany.id}/change-admin`,
          {
            mode: 'assign',
            user_id: values.assign_user_id,
          }
        );
      } else if (adminMode === 'create' && values.new_admin_email && values.new_admin_name) {
        await apiClient.post(
          `${apiBase}/api/v1/platform/companies/${editingCompany.id}/change-admin`,
          {
            mode: 'create',
            new_email: values.new_admin_email,
            new_name: values.new_admin_name,
          }
        );
      }

      message.success('公司已更新');
      setEditModalOpen(false);
      editForm.resetFields();
      setEditingCompany(null);
      setAdminMode('edit');
      fetchCompanies();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '更新失敗');
    }
  };

  const handleDeleteCompany = async (record: CompanyItem) => {
    try {
      await apiClient.delete(`${apiBase}/api/v1/platform/companies/${record.id}`);
      message.success(`公司 ${record.name} 已刪除`);
      fetchCompanies();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '刪除失敗');
    }
  };

  const openEditModal = (record: CompanyItem) => {
    setEditingCompany(record);
    setAdminMode('edit');
    editForm.setFieldsValue({
      name: record.name,
      code: record.code,
      max_users: record.max_users,
      admin_email: record.admin_email || '',
      admin_name: record.admin_name || '',
      ad_review_enabled: record.features?.ad_review_enabled ?? true,
      ai_rules_enabled: record.features?.ai_rules_enabled ?? false,
      ai_ruleset_upload_enabled: record.features?.ai_ruleset_upload_enabled ?? false,
      legaltech_library_enabled: record.features?.legaltech_library_enabled ?? false,
      ai_rule_optimization_enabled: record.features?.ai_rule_optimization_enabled ?? false,
      contract_comparison_enabled: record.features?.contract_comparison_enabled ?? false,
    });
    setEditModalOpen(true);
    fetchMembers(record.id);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '公司名稱', dataIndex: 'name', key: 'name', width: 160 },
    { title: '企業編碼', dataIndex: 'code', key: 'code', width: 100 },
    {
      title: '帳號數量',
      key: 'quota',
      width: 100,
      render: (_: unknown, record: CompanyItem) => (
        <span>
          {record.used_users} / {record.max_users ?? '不限'}
        </span>
      ),
    },
    {
      title: '本月 API 費用',
      key: 'monthly_cost',
      width: 130,
      render: (_: unknown, record: CompanyItem) => {
        const cost = record.monthly_cost_usd ?? 0;
        // 顯示金額加 10% 預算緩衝（DB 原始值不變）
        const displayCost = cost * 1.1;
        return (
          <span style={{ color: cost > 0 ? '#52c41a' : '#bbb', fontSize: 13 }}>
            US${displayCost.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: '狀態',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active: boolean, record: CompanyItem) => (
        <Popconfirm
          title={active ? '停用公司' : '啟用公司'}
          description={`確定要${active ? '停用' : '啟用'}「${record.name}」嗎？`}
          onConfirm={() => handleToggleActive(record)}
          okText='確定'
          cancelText='取消'
          okButtonProps={active ? { danger: true } : {}}
        >
          <Tag
            color={active ? '#0A4A5E' : '#00000040'}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {active ? '啟用' : '停用'}
          </Tag>
        </Popconfirm>
      ),
    },
    {
      title: 'Admin',
      key: 'admin',
      width: 280,
      render: (_: unknown, record: CompanyItem) =>
        record.admin_email ? (
          <span style={{ fontSize: 13 }}>
            {record.admin_name} ({record.admin_email})
          </span>
        ) : (
          <span style={{ color: '#999', fontSize: 13 }}>尚未建立</span>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: unknown, record: CompanyItem) => (
        <Space size='middle'>
          <Button size='small' icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm
            title='刪除公司'
            description={`確定要刪除「${record.name}」嗎？該公司所有使用者帳號將一併停用。`}
            onConfirm={() => handleDeleteCompany(record)}
            okText='確定刪除'
            cancelText='取消'
            okButtonProps={{ danger: true }}
          >
            <Button size='small' danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 非 Admin 的成員列表（供指派用）
  const nonAdminMembers = members.filter(m => !m.is_admin);

  return (
    <PageLayout>
      <div style={{ padding: '24px 40px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <h2 style={{ margin: 0 }}>公司管理</h2>
          <Space>
            <Button
              icon={<DollarOutlined />}
              onClick={() => setPricingModalOpen(true)}
            >
              模型定價管理
            </Button>
            <Button type='primary' icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
              新增公司
            </Button>
          </Space>
        </div>

        <Table
          dataSource={companies}
          columns={columns}
          rowKey='id'
          loading={loading}
          pagination={false}
        />
      </div>

      {/* 新增公司 Modal（含 Admin） */}
      <Modal
        title='新增公司'
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        okText='建立'
        cancelText='取消'
        width={520}
      >
        <Form form={createForm} layout='vertical' onFinish={handleCreateCompany}>
          <Form.Item
            name='name'
            label='公司名稱'
            rules={[{ required: true, message: '請輸入公司名稱' }]}
          >
            <Input placeholder='例：和泰汽車' />
          </Form.Item>
          <Form.Item
            name='code'
            label='企業編碼'
            rules={[{ required: true, message: '請輸入企業編碼' }]}
          >
            <Input placeholder='例：HOTAI（英文大寫）' />
          </Form.Item>
          <Form.Item name='max_users' label='帳號上限'>
            <InputNumber min={1} placeholder='留空代表不限' style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ borderTop: '1px solid #f0f0f0', margin: '16px 0', paddingTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
              Admin 管理員帳號
            </div>
          </div>
          <Form.Item
            name='admin_email'
            label='Admin Email'
            rules={[{ type: 'email', message: '請輸入有效的 Email' }]}
          >
            <Input placeholder='admin@company.com' />
          </Form.Item>
          <Form.Item name='admin_name' label='Admin 姓名'>
            <Input placeholder='管理員姓名' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改公司 Modal */}
      <Modal
        title={`修改公司 — ${editingCompany?.name || ''}`}
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
          setEditingCompany(null);
          setAdminMode('edit');
        }}
        onOk={() => editForm.submit()}
        okText='儲存'
        cancelText='取消'
        width={720}
      >
        <Form form={editForm} layout='vertical' onFinish={handleEditCompany}>
          {/* ── 公司資訊 ── */}
          <div style={{
            display: 'flex', alignItems: 'center',
            fontSize: 14, fontWeight: 700, color: '#1e293b',
            paddingLeft: 10, borderLeft: '3px solid #1e88a8',
            marginBottom: 12,
          }}>
            公司資訊
          </div>
          <Row gutter={16} style={{ marginBottom: 4 }}>
            <Col span={11}>
              <Form.Item
                name='name'
                label='公司名稱'
                rules={[{ required: true, message: '請輸入公司名稱' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name='code' label='企業編碼'>
                <Input disabled style={{ color: '#999' }} />
              </Form.Item>
            </Col>
            <Col flex='90px'>
              <Form.Item name='max_users' label='帳號上限'>
                <InputNumber min={1} placeholder='不限' style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* ── 功能開通 ── */}
          <div style={{
            display: 'flex', alignItems: 'center',
            fontSize: 14, fontWeight: 700, color: '#1e293b',
            paddingLeft: 10, borderLeft: '3px solid #1e88a8',
            marginBottom: 12,
          }}>
            功能開通
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* 合約比對 */}
            <div style={{
              background: '#fff',
              border: '1px solid var(--color-border, #f0f0f0)',
              borderRadius: 'var(--radius, 8px)',
              overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 18px',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: '#0d94881a', color: '#0d9488', fontSize: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <DiffOutlined />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>合約比對</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>定型化契約比對、合約版本比對</div>
                </div>
                <Form.Item name='contract_comparison_enabled' valuePropName='checked' style={{ margin: '0 0 0 16px', flexShrink: 0 }}>
                  <Switch />
                </Form.Item>
              </div>
            </div>

            {/* 全文審閱（含子功能） */}
            <div style={{
              background: '#fff',
              border: '1px solid var(--color-border, #f0f0f0)',
              borderRadius: 'var(--radius, 8px)',
              overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 18px',
                borderBottom: editAdReviewEnabled ? '1px solid var(--color-border, #f0f0f0)' : undefined,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: '#1e88a81a', color: '#1e88a8', fontSize: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AuditOutlined />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>全文審閱</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>AI 全文審閱功能總開關</div>
                </div>
                <Form.Item name='ad_review_enabled' valuePropName='checked' style={{ margin: '0 0 0 16px', flexShrink: 0 }}>
                  <Switch />
                </Form.Item>
              </div>

              {editAdReviewEnabled && (
                <div style={{ padding: '12px 16px 10px', background: '#f7f7f7' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {/* AI 生成規則集 */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 14px',
                      background: '#fff',
                      border: '1px solid var(--color-border, #f0f0f0)',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: '#e6f4f8', color: '#1e88a8', fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <UploadOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>AI 生成規則集</div>

                      </div>
                      <Form.Item name='ai_ruleset_upload_enabled' valuePropName='checked' style={{ margin: 0, flexShrink: 0 }}>
                        <Switch size='small' />
                      </Form.Item>
                    </div>

                    {/* AI 自動審閱 */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 14px',
                      background: '#fff',
                      border: '1px solid var(--color-border, #f0f0f0)',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: '#e6f4f8', color: '#1e88a8', fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        ✨
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>AI 自動審閱</div>

                      </div>
                      <Form.Item name='ai_rules_enabled' valuePropName='checked' style={{ margin: 0, flexShrink: 0 }}>
                        <Switch size='small' />
                      </Form.Item>
                    </div>

                    {/* 律果規則庫 */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 14px',
                      background: '#fff',
                      border: '1px solid var(--color-border, #f0f0f0)',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: '#e6f4f8', color: '#1e88a8', fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <AuditOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>律果規則庫</div>

                      </div>
                      <Form.Item name='legaltech_library_enabled' valuePropName='checked' style={{ margin: 0, flexShrink: 0 }}>
                        <Switch size='small' />
                      </Form.Item>
                    </div>

                    {/* AI 規則優化 */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 14px',
                      background: '#fff',
                      border: '1px solid var(--color-border, #f0f0f0)',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: '#e6f4f8', color: '#1e88a8', fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <BulbOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>AI 規則優化</div>

                      </div>
                      <Form.Item name='ai_rule_optimization_enabled' valuePropName='checked' style={{ margin: 0, flexShrink: 0 }}>
                        <Switch size='small' />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Admin 管理員 ── */}
          <div style={{
            display: 'flex', alignItems: 'center',
            fontSize: 14, fontWeight: 700, color: '#1e293b',
            paddingLeft: 10, borderLeft: '3px solid #1e88a8',
            marginTop: 20, marginBottom: 12,
          }}>
            Admin 管理員
          </div>
          {editingCompany?.admin_email && (
            <div style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
              目前：{editingCompany.admin_name} ({editingCompany.admin_email})
            </div>
          )}

          <Radio.Group
            value={adminMode}
            onChange={e => setAdminMode(e.target.value)}
            style={{ marginBottom: 16 }}
          >
            <Radio value='edit'>修改現有 Admin</Radio>
            <Radio value='assign'>指派現有成員</Radio>
            <Radio value='create'>建立新帳號</Radio>
          </Radio.Group>

          {adminMode === 'edit' && (
            <>
              <Form.Item
                name='admin_email'
                label='Admin Email'
                rules={[{ type: 'email', message: '請輸入有效的 Email' }]}
              >
                <Input placeholder='admin@company.com' />
              </Form.Item>
              <Form.Item name='admin_name' label='Admin 姓名'>
                <Input placeholder='管理員姓名' />
              </Form.Item>
            </>
          )}

          {adminMode === 'assign' && (
            <Form.Item name='assign_user_id' label='選擇成員'>
              <Select
                placeholder='選擇要指派為 Admin 的成員'
                loading={membersLoading}
                options={nonAdminMembers.map(m => ({
                  value: m.id,
                  label: `${m.name} (${m.email})`,
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                }
                notFoundContent={nonAdminMembers.length === 0 ? '無其他成員可指派' : undefined}
              />
            </Form.Item>
          )}

          {adminMode === 'create' && (
            <>
              <Form.Item
                name='new_admin_email'
                label='新 Admin Email'
                rules={[{ type: 'email', message: '請輸入有效的 Email' }]}
              >
                <Input placeholder='new-admin@company.com' />
              </Form.Item>
              <Form.Item name='new_admin_name' label='新 Admin 姓名'>
                <Input placeholder='新管理員姓名' />
              </Form.Item>
              <div style={{ fontSize: 12, color: '#999', marginTop: -12, marginBottom: 8 }}>
                舊 Admin 將自動降級為一般成員
              </div>
            </>
          )}
        </Form>
      </Modal>

      {/* 模型定價管理 Modal（Super Admin） */}
      <ModelPricingModal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
      />
    </PageLayout>
  );
};

export default CompanyManagementPage;
